import type { LanguagePairDefinition, LanguageProfile } from './types'
import { sampleWordsFor } from './sample-words'

/**
 * Build a directional LanguageProfile from a pair definition.
 *
 * @param pairDef    - The LanguagePairDefinition (e.g. en-zh pair)
 * @param targetLang - Which language the user is learning (e.g. 'en' or 'zh')
 * @returns A fully-populated LanguageProfile
 */
export function buildProfile(
  pairDef: LanguagePairDefinition,
  targetLang: string,
): LanguageProfile {
  const target = pairDef.languages[targetLang]
  if (!target) throw new Error(`Unknown targetLang "${targetLang}" for pair "${pairDef.id}"`)

  const nativeLangCode = Object.keys(pairDef.languages).find(k => k !== targetLang)
  if (!nativeLangCode) throw new Error(`Pair "${pairDef.id}" must have exactly 2 languages`)
  const native = pairDef.languages[nativeLangCode]

  return {
    id: `${targetLang}-${nativeLangCode}`,
    targetLang: target.lang,
    nativeLang: native.lang,
    targetLangName: target.langName,
    nativeLangName: native.langName,

    promptConfig: {
      frontLabel: target.langName,
      backLabel: native.langName,
      classifierHint: `Input may be in ${native.langName} or ${target.langName}. ${target.promptConfig.classifierHint}`,
      languageHints: target.promptConfig.languageHints,
    },

    phonetic: target.phonetic,

    tts: {
      voices: target.tts.voices,
      nativeVoice: native.tts.voices[0].lang,
      detectScript: native.tts.detectScript,
    },

    ui: {
      ...native.ui,
      // Starter words come from the TARGET language — the word the learner
      // taps is the word the card teaches (u2: en-ko user + "charcoal").
      // Never native.ui, which would offer L1 words for an L2 card.
      sampleWords: sampleWordsFor(target.lang),
      directionLabel: {
        forward: `${target.shortCode}→${native.shortCode}`,
        reverse: `${native.shortCode}→${target.shortCode}`,
      },
    },

    intentOverrides: target.intentOverrides,
    // cardMeta describes TARGET-language grammar (what the learner is learning).
    // No native fallback — a Chinese native learning English must NOT receive the
    // Chinese measureWord/pinyin/variants prompt. Mirrors `phonetic` (line ~35)
    // which is also target-only. The old `?? native.cardMeta` leaked L1 metadata
    // into L2-target generation.
    cardMeta: target.cardMeta,
  }
}
